import { Head, router } from "@inertiajs/react"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { MainContainer } from "@/components/ui/container"
import { StaffLayout } from "@/components/layouts/staff-layout"
import { t } from "@/lib/i18n"

/**
 * The mailed link, opened.
 *
 * Four states, and the server decided which one — the page never inspects an
 * expiry or compares an address, because the same reasoning that makes a dead
 * token indistinguishable from a token that never existed has to hold on this
 * side too. `expired` arrives carrying nothing but itself: no group name, no
 * inviter, not even the address it was sent to. Whoever is holding a dead link
 * may be anyone.
 *
 * `wrong_account` is the one state that names an address, and it names only the
 * one the link was already mailed to. That visitor needs to be told which
 * account to use, and telling them leaks nothing they were not already holding.
 *
 * No layout chrome beyond the shell: this person may hold no restaurant at all,
 * so the navigation has nothing to offer them yet.
 */

type Props = {
  invite:
    | {
        state: "acceptable"
        email: string
        organizationName: string
        roleLabel: string
        inviterName: string | null
        token: string
      }
    | { state: "wrong_account"; email: string; token: string }
    | { state: "expired"; token: string }
}

function tInvite(key: string, options: Record<string, string | number> = {}) {
  return t(`ui.react.pages.staff.organization_invites.show.${key}`, options)
}

export default function StaffOrganizationInviteShow({ invite }: Props) {
  const title = tInvite("page_title")

  return (
    <>
      <Head title={title} />

      <MainContainer size="sm">
        <Card density="compact" data-testid="organization-invite-card">
          <CardHeader className="border-b">
            <CardTitle>{title}</CardTitle>
          </CardHeader>

          {invite.state === "acceptable" && (
            <CardContent className="space-y-4" data-testid="organization-invite-acceptable">
              <div className="space-y-1">
                <p className="text-foreground text-sm">
                  {invite.inviterName
                    ? tInvite("body", {
                        inviter: invite.inviterName,
                        organization: invite.organizationName,
                      })
                    : tInvite("body_no_inviter", { organization: invite.organizationName })}
                </p>
                <p className="text-muted-foreground text-xs">
                  {tInvite("role", { role: invite.roleLabel })}
                </p>
                <p className="text-muted-foreground text-xs">
                  {tInvite("as_account", { email: invite.email })}
                </p>
              </div>

              {/* Accepting grants standing in the group and nothing else. Saying
                  so here is not fine print: an invitee who expects to see the
                  branches would otherwise read an empty screen as a bug. */}
              <p className="text-muted-foreground text-xs">{tInvite("grants")}</p>

              <div className="flex flex-wrap gap-2">
                <Button
                  className="h-11 md:h-9"
                  data-testid="organization-invite-accept"
                  onClick={() => router.post(`/organization_invites/${invite.token}/accept`)}
                >
                  {tInvite("accept")}
                </Button>

                <Button
                  variant="secondary"
                  className="h-11 md:h-9"
                  data-testid="organization-invite-decline"
                  onClick={() => router.post(`/organization_invites/${invite.token}/decline`)}
                >
                  {tInvite("decline")}
                </Button>
              </div>
            </CardContent>
          )}

          {invite.state === "wrong_account" && (
            <CardContent className="space-y-4" data-testid="organization-invite-wrong-account">
              <p className="text-foreground text-sm">
                {tInvite("wrong_account", { email: invite.email })}
              </p>

              <Button
                variant="secondary"
                className="h-11 md:h-9"
                data-testid="organization-invite-sign-out"
                onClick={() => router.delete("/session")}
              >
                {tInvite("sign_out")}
              </Button>
            </CardContent>
          )}

          {invite.state === "expired" && (
            <CardContent data-testid="organization-invite-expired">
              <p className="text-foreground text-sm">{tInvite("expired")}</p>
            </CardContent>
          )}
        </Card>
      </MainContainer>
    </>
  )
}

StaffOrganizationInviteShow.layout = (page: React.ReactNode) => <StaffLayout>{page}</StaffLayout>
